import React, { useEffect, useState, createContext, useContext } from 'react';
import { AuthProvider, TAuthConfig, IAuthContext, useAuthContext as useRealAuthContext } from 'react-oauth2-code-pkce';
import * as z from 'zod';
import axios from 'axios';

const frontendConfigSchema = z.object({
    clientId: z.string().nonempty(),
    domain: z.url()
})

const openIdConfigSchema = z.object({
    authorization_endpoint: z.url(),
    token_endpoint: z.url(),
    userinfo_endpoint: z.url(),
    jwks_uri: z.url()
})

type BootstrapConfig = {
    frontend: z.infer<typeof frontendConfigSchema>,
    openId: z.infer<typeof openIdConfigSchema>
} | { localDevMode: true }

// Mock auth context for local dev mode
const mockAuthContext: IAuthContext = {
    token: 'local-dev-token',
    logIn: () => {},
    logOut: () => {},
    error: null,
    loginInProgress: false,
    idToken: 'local-dev-id-token',
    tokenData: { sub: 'local-dev-user', name: 'Local Dev User', email: 'dev@localhost' },
    idTokenData: { sub: 'local-dev-user', name: 'Local Dev User', email: 'dev@localhost' },
    login: () => {},
};

const LocalDevAuthContext = createContext<IAuthContext>(mockAuthContext);

async function loadBootstrapConfig(): Promise<BootstrapConfig> {
    while (true) {
        try {
            const feResponse = await axios.get('/api/frontend');

            // Check if backend is in local dev mode
            if (feResponse.data.localDevMode) {
                console.log('[AUTH] Backend is in local dev mode - skipping OAuth');
                return { localDevMode: true };
            }

            const frontend = frontendConfigSchema.parse(feResponse.data);
            const oidResponse = await axios.get(`${frontend.domain}/.well-known/openid-configuration`);
            const openId = openIdConfigSchema.parse(oidResponse.data);

            return { frontend, openId };
        } catch (error) {
            console.error('Failed to load frontend config, retrying in 5 seconds...', error);
            await new Promise(resolve => setTimeout(resolve, 5000));
        }
    }
}

// Track which mode we're in
let isLocalDevMode = false;

// Unified useAuthContext that works in both modes
export function useAuthContext(): IAuthContext {
    if (isLocalDevMode) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useContext(LocalDevAuthContext);
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useRealAuthContext();
}

export function AuthenticationProvider(props: { children: React.ReactElement }): React.ReactElement {
    const [bootstrapConfig, setBootstrapConfig] = useState<BootstrapConfig | null>(null);
    useEffect(() => {
        loadBootstrapConfig().then(setBootstrapConfig);
        return () => { };
    }, []);

    if (!bootstrapConfig) {
        return <div>Loading...</div>;
    }

    // Local dev mode - skip OAuth entirely
    if ('localDevMode' in bootstrapConfig) {
        isLocalDevMode = true;
        return (
            <LocalDevAuthContext.Provider value={mockAuthContext}>
                {props.children}
            </LocalDevAuthContext.Provider>
        );
    }

    isLocalDevMode = false;
    const reactPkceConfig: TAuthConfig = {
        clientId: bootstrapConfig.frontend.clientId,
        authorizationEndpoint: bootstrapConfig.openId.authorization_endpoint,
        tokenEndpoint: bootstrapConfig.openId.token_endpoint,
        redirectUri: window.location.origin + window.location.pathname,
        scope: 'openid profile email',
    }

    return (
        <AuthProvider authConfig={reactPkceConfig}>
            {props.children}
        </AuthProvider>)
}
